import { NetworkStatus, gql, useQuery } from '@apollo/client';
import { Button, Center, Flex, Heading, Text, VStack } from '@chakra-ui/react';
import * as React from 'react';

import { Post } from '@/components/Post';
import { PostPlaceholderFeed } from '@/components/placeholder-feed';
import { Card } from '@/imports/ui/chakra/components/Card';

import {
  RecentActivity_ActivityByAuthorIdV2,
  RecentActivity_ActivityByAuthorIdV2Variables,
} from './graphql-types/RecentActivity_ActivityByAuthorIdV2';

const RECENT_ACTIVITY_PAGE_SIZE = 5;

const GET_POSTS = gql`
  query RecentActivity_ActivityByAuthorIdV2($input: ActivityByAuthorInputV2!) {
    activityByAuthorIdV2(input: $input) {
      posts {
        post {
          _id
          ...Post_PostV2Fragment
        }
        numCommentsByUser
      }
      hasMore
    }
  }
  ${Post.postFragment}
`;

export const RecentActivity: React.FC<{ authorId: string }> = ({
  authorId,
}) => {
  const query = useQuery<
    RecentActivity_ActivityByAuthorIdV2,
    RecentActivity_ActivityByAuthorIdV2Variables
  >(GET_POSTS, {
    variables: {
      input: {
        authorId,
        limit: RECENT_ACTIVITY_PAGE_SIZE,
        offset: 0,
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  if (query.networkStatus === NetworkStatus.loading) {
    return <PostPlaceholderFeed w="full" />;
  }

  if (!query.data?.activityByAuthorIdV2.posts.length) {
    return (
      <Card px="4" py="6" mb="4">
        <Flex mb="4" justify="space-between">
          <Heading as="h4" size="md">
            Recent Activity
          </Heading>
        </Flex>

        <Center color="gray.500" h="48" bg="gray.50" borderRadius="base">
          <Text>There are no discussions yet</Text>
        </Center>
      </Card>
    );
  }

  return (
    <Card px="4" py="6" mb="4">
      <Flex mb="4" justify="space-between">
        <Heading as="h4" size="md">
          Recent Activity
        </Heading>
      </Flex>

      <VStack spacing="4">
        {query.data.activityByAuthorIdV2.posts.map((postWrapper) => {
          const post = postWrapper.post;
          const hasCommented = !!(
            postWrapper?.numCommentsByUser && postWrapper.numCommentsByUser > 0
          );

          return (
            <Post
              post={post}
              key={post._id}
              options={{ showCommentedHeader: hasCommented }}
            />
          );
        })}

        {(query.data.activityByAuthorIdV2.hasMore ||
          query.networkStatus === NetworkStatus.fetchMore) && (
          <Flex justifyContent="center" p="4">
            <Button
              isLoading={query.networkStatus === NetworkStatus.fetchMore}
              variant="link"
              colorScheme="black"
              onClick={() =>
                query.fetchMore({
                  variables: {
                    input: {
                      authorId,
                      limit: RECENT_ACTIVITY_PAGE_SIZE,
                      offset: query.data?.activityByAuthorIdV2.posts.length,
                    },
                  },
                })
              }
            >
              See More
            </Button>
          </Flex>
        )}
      </VStack>
    </Card>
  );
};
